import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import { config } from '../config';

const userPool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.clientId
});

export interface AuthUser {
  email: string;
  token: string;
}

export interface MFASetupData {
  secretCode: string;
  qrCodeUrl: string;
  email: string;
}

export interface MFARequiredData {
  email: string;
}

export type AuthResult =
  | { type: 'SUCCESS'; user: AuthUser }
  | { type: 'MFA_SETUP'; data: MFASetupData; cognitoUser: CognitoUser }
  | { type: 'MFA_REQUIRED'; data: MFARequiredData; cognitoUser: CognitoUser };

export const authService = {
  login: (email: string, password: string): Promise<AuthResult> => {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const token = session.getIdToken().getJwtToken();
          resolve({
            type: 'SUCCESS',
            user: { email, token }
          });
        },
        onFailure: (err) => {
          reject(err);
        },
        mfaSetup: (challengeName, challengeParameters) => {
          // User needs to set up MFA for the first time
          cognitoUser.associateSoftwareToken({
            associateSecretCode: (secretCode) => {
              // Generate QR code URL for Google Authenticator
              const appName = 'CMS Roster';
              const qrCodeUrl = `otpauth://totp/${encodeURIComponent(appName)}:${encodeURIComponent(email)}?secret=${secretCode}&issuer=${encodeURIComponent(appName)}`;

              resolve({
                type: 'MFA_SETUP',
                data: { secretCode, qrCodeUrl, email },
                cognitoUser
              });
            },
            onFailure: (err) => {
              reject(err);
            }
          });
        },
        totpRequired: (challengeName, challengeParameters) => {
          // User has MFA set up and needs to enter TOTP code
          resolve({
            type: 'MFA_REQUIRED',
            data: { email },
            cognitoUser
          });
        },
        mfaRequired: (challengeName, challengeParameters) => {
          // User has MFA set up and needs to enter MFA code
          resolve({
            type: 'MFA_REQUIRED',
            data: { email },
            cognitoUser
          });
        }
      });
    });
  },

  verifyMFASetup: (cognitoUser: CognitoUser, code: string): Promise<AuthUser> => {
    return new Promise((resolve, reject) => {
      cognitoUser.verifySoftwareToken(code, 'My TOTP Device', {
        onSuccess: (session: CognitoUserSession) => {
          const token = session.getIdToken().getJwtToken();
          const email = session.getIdToken().payload.email;
          resolve({ email, token });
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  },

  submitMFACode: (cognitoUser: CognitoUser, code: string): Promise<AuthUser> => {
    return new Promise((resolve, reject) => {
      cognitoUser.sendMFACode(code, {
        onSuccess: (session: CognitoUserSession) => {
          const token = session.getIdToken().getJwtToken();
          const email = session.getIdToken().payload.email;
          resolve({ email, token });
        },
        onFailure: (err) => {
          reject(err);
        }
      }, 'SOFTWARE_TOKEN_MFA');
    });
  },

  logout: () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  },

  getCurrentUser: (): Promise<CognitoUser | null> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err);
          return;
        }
        
        if (session.isValid()) {
          resolve(cognitoUser);
        } else {
          resolve(null);
        }
      });
    });
  },

  getToken: (): Promise<string | null> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err);
          return;
        }
        
        if (session.isValid()) {
          resolve(session.getIdToken().getJwtToken());
        } else {
          resolve(null);
        }
      });
    });
  }
};

